import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useAppServices } from "../../../../../../../hook/services";
import { toast } from "react-toastify";
import { IconsArray } from "../../../../../constants/Icons";
import {
  agencyTargetLocations,
  targetLocations,
} from "../../../../../constants/TargetLocations";

const DEFAULT_COLOR = "#4f46e5";
const DEFAULT_TEXT = "My Button";

const AddDashboardButton = ({
  handleRefresh,
  data,
  editIndex = null,
  selectedButton = null,
}) => {
  const AppService = useAppServices();
  const { companyId, type } = useParams();
  const locationsPaths =
    type === "agency" ? agencyTargetLocations : targetLocations;

  const handler =
    type === "agency" || type === "location"
      ? AppService.sidenav_customizer_main_level
      : type === "group"
      ? AppService.sidenav_customizer_custom_level
      : AppService.sidenav_customizer_saas_level;

  const isEdit = editIndex !== null && selectedButton;

  const [open, setOpen] = useState(false);
  const [buttonText, setButtonText] = useState(DEFAULT_TEXT);
  const [buttonColor, setButtonColor] = useState(DEFAULT_COLOR);
  const [buttonType, setButtonType] = useState("TARGET");
  const [linkValue, setLinkValue] = useState("");
  const [targetLocation, setTargetLocation] = useState("");
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [icon, setIcon] = useState("");
  const [iconFamily, setIconFamily] = useState("fas");
  const [iconSearch, setIconSearch] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    if (!isEdit) resetForm();
  };

  useEffect(() => {
    if (selectedButton) {
      setButtonText(selectedButton.text || DEFAULT_TEXT);
      setButtonColor(selectedButton.color || DEFAULT_COLOR);
      setButtonType(selectedButton.type || "TARGET");
      setLinkValue(selectedButton.link || selectedButton.linkTarget || "");
      setTargetLocation(selectedButton.targetLocation || "");
      setShowLinkInput(!!selectedButton.linkTarget);
      setIcon(selectedButton.icon || "");
      setIconFamily(selectedButton.iconFamily || "fas");
    }
  }, [selectedButton]);

  const resetForm = () => {
    setButtonText(DEFAULT_TEXT);
    setButtonColor(DEFAULT_COLOR);
    setButtonType("TARGET");
    setLinkValue("");
    setTargetLocation("");
    setShowLinkInput(false);
    setIcon("");
    setIconFamily("fas");
    setIconSearch("");
  };

  const filteredIcons = useMemo(() => {
    const term = iconSearch.trim().toLowerCase();
    if (!term) return IconsArray;
    return IconsArray.filter((i) => i.name.toLowerCase().includes(term));
  }, [iconSearch]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!buttonText.trim()) {
      toast.warning("Button text is required.");
      return;
    }
    if (buttonType === "LINK" && !linkValue.trim()) {
      toast.warning("Please provide a valid link.");
      return;
    }
    if (buttonType === "TARGET" && !showLinkInput && !targetLocation) {
      toast.warning("Please select a target location.");
      return;
    }
    if (buttonType === "TARGET" && showLinkInput && !linkValue.trim()) {
      toast.warning("Please provide a target link.");
      return;
    }

    const button = {
      text: buttonText,
      color: buttonColor,
      type: buttonType,
      link: buttonType === "LINK" ? linkValue : null,
      targetLocation: buttonType === "TARGET" ? targetLocation : null,
      linkTarget: buttonType === "TARGET" && showLinkInput ? linkValue : null,
      icon: icon || null,
      iconFamily: icon ? iconFamily : null,
    };

    const dashboard_button = data?.dashboard_button
      ? [...data.dashboard_button]
      : [];

    if (editIndex !== null && editIndex >= 0) {
      dashboard_button[editIndex] = button;
    } else {
      dashboard_button.push(button);
    }

    const payload = {
      dashboard_button,
      companyId,
      type,
      ...(data?._id && { _id: data._id }),
    };

    try {
      setSubmitting(true);
      const { response } = await handler[data?._id ? "update" : "create"]({
        payload,
      });

      if (response) {
        toast.success(isEdit ? "Button updated!" : "Button added!");
        handleRefresh();
        setOpen(false);
        if (!isEdit) resetForm();
      } else {
        toast.error("Failed to save button.");
      }
    } catch (error) {
      console.error("Error saving button:", error);
      toast.error("An error occurred. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {isEdit ? (
        <button
          className="btn d-inline-flex align-items-center justify-content-center gap-1"
          title="Edit"
          onClick={handleOpen}
          style={{
            borderRadius: 8,
            background: "#eef2ff",
            color: "#4338ca",
            border: "1px solid #c7d2fe",
            fontSize: 12,
            fontWeight: 500,
            padding: "6px 10px",
          }}
        >
          <i className="bi bi-pencil-square" />
          Edit
        </button>
      ) : (
        <button
          className="btn btn-primary d-inline-flex align-items-center gap-2"
          onClick={handleOpen}
          style={{ borderRadius: 10, fontWeight: 500 }}
        >
          <i className="bi bi-plus-lg" />
          Add Dashboard Button
        </button>
      )}

      {open && (
        <div
          className="modal fade show d-block"
          tabIndex={-1}
          style={{ backgroundColor: "rgba(15, 23, 42, 0.55)", zIndex: 2000 }}
          onClick={(e) => {
            if (e.target === e.currentTarget) handleClose();
          }}
        >
          <style>{`
            .db-modal .modal-dialog {
              max-height: calc(100vh - 3rem);
              margin-top: 1.5rem;
              margin-bottom: 1.5rem;
            }
            .db-modal .modal-content {
              border-radius: 16px;
              overflow: hidden;
              border: none;
              box-shadow: 0 24px 48px rgba(15, 23, 42, 0.18);
              max-height: calc(100vh - 3rem);
              display: flex;
              flex-direction: column;
            }
            .db-modal form {
              display: flex;
              flex-direction: column;
              flex: 1 1 auto;
              min-height: 0;
              overflow: hidden;
            }
            .db-modal .modal-body {
              flex: 1 1 auto;
              overflow-y: auto;
              min-height: 0;
            }
            .db-modal .modal-body::-webkit-scrollbar { width: 8px; }
            .db-modal .modal-body::-webkit-scrollbar-track { background: transparent; }
            .db-modal .modal-body::-webkit-scrollbar-thumb {
              background: rgba(15, 23, 42, 0.18);
              border-radius: 8px;
            }
            .db-modal .modal-body::-webkit-scrollbar-thumb:hover {
              background: rgba(15, 23, 42, 0.32);
            }
            .db-modal .db-header { flex-shrink: 0; }
            .db-modal .db-header {
              background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
              color: #fff;
              padding: 18px 22px;
              display: flex;
              align-items: center;
              justify-content: space-between;
            }
            .db-modal .db-header h5 {
              margin: 0;
              font-weight: 600;
              font-size: 18px;
              display: flex;
              align-items: center;
              gap: 10px;
            }
            .db-modal .db-header small {
              color: rgba(255,255,255,0.8);
              font-size: 12px;
            }
            .db-modal .db-close {
              background: rgba(255,255,255,0.15);
              border: none;
              color: #fff;
              width: 32px;
              height: 32px;
              border-radius: 8px;
              display: inline-flex;
              align-items: center;
              justify-content: center;
              transition: background 0.15s;
            }
            .db-modal .db-close:hover { background: rgba(255,255,255,0.28); }
            .db-modal .db-section-title {
              font-size: 12px;
              font-weight: 600;
              text-transform: uppercase;
              letter-spacing: 0.04em;
              color: #475569;
              margin-bottom: 10px;
            }
            .db-modal .db-card {
              background: #ffffff;
              border: 1px solid #e2e8f0;
              border-radius: 12px;
              padding: 16px;
            }
            .db-modal .db-preview {
              background: linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%);
              border: 1px dashed #c7d2fe;
              border-radius: 12px;
              padding: 22px;
              display: flex;
              align-items: center;
              justify-content: center;
              min-height: 110px;
            }
            .db-modal .db-preview-btn {
              border-radius: 8px;
              padding: 8px 16px;
              font-weight: 500;
              font-size: 13px;
              color: #fff;
              border: none;
              display: inline-flex;
              align-items: center;
              gap: 8px;
              box-shadow: 0 4px 12px rgba(15, 23, 42, 0.15);
            }
            .db-modal .form-label {
              font-size: 13px;
              font-weight: 500;
              color: #334155;
              margin-bottom: 6px;
            }
            .db-modal .form-control,
            .db-modal .form-select {
              border-radius: 8px;
              border-color: #e2e8f0;
              font-size: 14px;
            }
            .db-modal .form-control:focus,
            .db-modal .form-select:focus {
              border-color: #818cf8;
              box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
            }
            .db-modal .color-picker-wrap {
              display: flex;
              align-items: center;
              gap: 10px;
            }
            .db-modal .color-picker-wrap input[type="color"] {
              width: 44px;
              height: 38px;
              padding: 2px;
              border-radius: 8px;
              border: 1px solid #e2e8f0;
              cursor: pointer;
              flex-shrink: 0;
            }
            .db-modal .type-toggle {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 8px;
            }
            .db-modal .type-toggle button {
              border: 1px solid #e2e8f0;
              background: #fff;
              border-radius: 10px;
              padding: 10px 12px;
              font-size: 13px;
              font-weight: 500;
              color: #475569;
              transition: all 0.15s;
              display: flex;
              align-items: center;
              gap: 8px;
              justify-content: center;
            }
            .db-modal .type-toggle button.active {
              background: #eef2ff;
              border-color: #818cf8;
              color: #4338ca;
            }
            .db-modal .icon-search {
              position: relative;
              margin-bottom: 10px;
            }
            .db-modal .icon-search .bi-search {
              position: absolute;
              left: 12px;
              top: 50%;
              transform: translateY(-50%);
              color: #94a3b8;
            }
            .db-modal .icon-search input {
              padding-left: 36px;
            }
            .db-modal .icon-grid {
              display: grid;
              grid-template-columns: repeat(8, 1fr);
              gap: 6px;
              max-height: 220px;
              overflow-y: auto;
              padding: 6px;
              background: #f8fafc;
              border: 1px solid #e2e8f0;
              border-radius: 10px;
            }
            .db-modal .icon-cell {
              aspect-ratio: 1;
              border: 1px solid transparent;
              background: #fff;
              border-radius: 8px;
              display: flex;
              align-items: center;
              justify-content: center;
              cursor: pointer;
              transition: all 0.12s;
              color: #475569;
              font-size: 16px;
            }
            .db-modal .icon-cell:hover {
              border-color: #c7d2fe;
              transform: translateY(-1px);
              color: #4338ca;
            }
            .db-modal .icon-cell.active {
              background: #eef2ff;
              border-color: #4f46e5;
              color: #4338ca;
              box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.18);
            }
            .db-modal .icon-info {
              font-size: 12px;
              color: #64748b;
              margin-top: 8px;
              display: flex;
              align-items: center;
              justify-content: space-between;
            }
            .db-modal .icon-clear {
              border: none;
              background: transparent;
              color: #ef4444;
              font-size: 12px;
              font-weight: 500;
              padding: 0;
            }
            .db-modal .icon-clear:hover { text-decoration: underline; }
            .db-modal .toggle-row {
              display: flex;
              align-items: center;
              gap: 8px;
              font-size: 13px;
              color: #475569;
              cursor: pointer;
              user-select: none;
            }
            .db-modal .modal-footer {
              border-top: 1px solid #e2e8f0;
              padding: 14px 22px;
              background: #f8fafc;
              flex-shrink: 0;
            }
            .db-modal .btn-cancel {
              background: transparent;
              color: #64748b;
              border: 1px solid #e2e8f0;
              border-radius: 8px;
              font-weight: 500;
            }
            .db-modal .btn-cancel:hover {
              background: #f1f5f9;
              color: #1e293b;
            }
            .db-modal .btn-save {
              background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
              border: none;
              color: #fff;
              border-radius: 8px;
              font-weight: 500;
              padding: 8px 22px;
            }
            .db-modal .btn-save:hover { opacity: 0.92; color: #fff; }
            .db-modal .btn-save:disabled { opacity: 0.6; }
          `}</style>

          <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg db-modal">
            <div className="modal-content">
              <div className="db-header">
                <div>
                  <h5>
                    <i
                      className={`bi ${
                        isEdit ? "bi-pencil-square" : "bi-plus-square"
                      }`}
                    />
                    {isEdit ? "Edit Dashboard Button" : "Add Dashboard Button"}
                  </h5>
                  <small>
                    Configure how this button looks and what it does when
                    clicked.
                  </small>
                </div>
                <button
                  type="button"
                  className="db-close"
                  onClick={handleClose}
                  aria-label="Close"
                >
                  <i className="bi bi-x-lg" />
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="modal-body p-4">
                  {/* Live preview */}
                  <div className="db-preview mb-4">
                    <button
                      type="button"
                      className="db-preview-btn"
                      style={{ background: buttonColor }}
                      tabIndex={-1}
                    >
                      {icon && (
                        <i
                          className={`${iconFamily} fa-${icon}`}
                          style={{ fontSize: 14 }}
                        />
                      )}
                      <span>{buttonText || "Button preview"}</span>
                    </button>
                  </div>

                  {/* Identity */}
                  <div className="db-card mb-3">
                    <div className="db-section-title">
                      <i className="bi bi-tag me-1" /> Identity
                    </div>
                    <div className="row g-3">
                      <div className="col-md-7">
                        <label className="form-label">Button text</label>
                        <input
                          type="text"
                          className="form-control"
                          value={buttonText}
                          onChange={(e) => setButtonText(e.target.value)}
                          placeholder="e.g. Open Help Desk"
                          required
                        />
                      </div>
                      <div className="col-md-5">
                        <label className="form-label">Background color</label>
                        <div className="color-picker-wrap">
                          <input
                            type="color"
                            value={buttonColor}
                            onChange={(e) => setButtonColor(e.target.value)}
                          />
                          <input
                            type="text"
                            className="form-control"
                            value={buttonColor}
                            onChange={(e) => setButtonColor(e.target.value)}
                            placeholder="#4f46e5"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Icon */}
                  <div className="db-card mb-3">
                    <div className="db-section-title">
                      <i className="bi bi-emoji-smile me-1" /> Icon (optional)
                    </div>
                    <div className="icon-search">
                      <i className="bi bi-search" />
                      <input
                        type="text"
                        className="form-control"
                        value={iconSearch}
                        onChange={(e) => setIconSearch(e.target.value)}
                        placeholder="Search icons..."
                      />
                    </div>

                    <div className="icon-grid">
                      {filteredIcons.length === 0 ? (
                        <div
                          className="text-center text-muted small py-3"
                          style={{ gridColumn: "1 / -1" }}
                        >
                          No icons match "{iconSearch}"
                        </div>
                      ) : (
                        filteredIcons.map((ic) => (
                          <div
                            key={`${ic.fontFamily}-${ic.name}`}
                            className={`icon-cell ${
                              icon === ic.name && iconFamily === ic.fontFamily
                                ? "active"
                                : ""
                            }`}
                            onClick={() => {
                              setIcon(ic.name);
                              setIconFamily(ic.fontFamily);
                            }}
                            title={ic.name}
                          >
                            <i className={`${ic.fontFamily} fa-${ic.name}`} />
                          </div>
                        ))
                      )}
                    </div>

                    <div className="icon-info">
                      <span>
                        {icon ? (
                          <>
                            Selected: <strong>{icon}</strong>
                          </>
                        ) : (
                          <span className="text-muted">No icon selected</span>
                        )}
                      </span>
                      {icon && (
                        <button
                          type="button"
                          className="icon-clear"
                          onClick={() => setIcon("")}
                        >
                          <i className="bi bi-x-circle me-1" />
                          Remove icon
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Behaviour */}
                  <div className="db-card">
                    <div className="db-section-title">
                      <i className="bi bi-arrow-up-right-circle me-1" /> Click
                      behaviour
                    </div>

                    <div className="type-toggle mb-3">
                      <button
                        type="button"
                        className={buttonType === "TARGET" ? "active" : ""}
                        onClick={() => {
                          setButtonType("TARGET");
                          setShowLinkInput(false);
                        }}
                      >
                        <i className="bi bi-bullseye" />
                        Internal target
                      </button>
                      <button
                        type="button"
                        className={buttonType === "LINK" ? "active" : ""}
                        onClick={() => {
                          setButtonType("LINK");
                          setShowLinkInput(false);
                        }}
                      >
                        <i className="bi bi-link-45deg" />
                        External link
                      </button>
                    </div>

                    {buttonType === "TARGET" && (
                      <>
                        <label className="toggle-row mb-3">
                          <input
                            type="checkbox"
                            checked={showLinkInput}
                            onChange={(e) =>
                              setShowLinkInput(e.target.checked)
                            }
                          />
                          Use a custom URL instead of a preset GHL location
                        </label>
                        {!showLinkInput ? (
                          <div>
                            <label className="form-label">
                              Target location
                            </label>
                            <select
                              className="form-select"
                              value={targetLocation}
                              onChange={(e) =>
                                setTargetLocation(e.target.value)
                              }
                              required
                            >
                              <option value="">Select a location...</option>
                              {locationsPaths.map((loc, idx) => (
                                <option key={idx} value={loc}>
                                  {loc}
                                </option>
                              ))}
                            </select>
                          </div>
                        ) : (
                          <div>
                            <label className="form-label">Custom URL</label>
                            <input
                              type="url"
                              className="form-control"
                              value={linkValue}
                              onChange={(e) => setLinkValue(e.target.value)}
                              placeholder="https://..."
                            />
                          </div>
                        )}
                      </>
                    )}

                    {buttonType === "LINK" && (
                      <div>
                        <label className="form-label">External URL</label>
                        <input
                          type="url"
                          className="form-control"
                          value={linkValue}
                          onChange={(e) => setLinkValue(e.target.value)}
                          placeholder="https://..."
                          required
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-cancel"
                    onClick={handleClose}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-save"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        />
                        Saving...
                      </>
                    ) : isEdit ? (
                      "Update button"
                    ) : (
                      "Create button"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AddDashboardButton;
